import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { saveAs } from 'file-saver';

const API_URL = process.env.REACT_APP_API_URL;
const GET_REPORTS_URL = `${API_URL}/dynamicreport`;
const GET_PARAMETERS_URL = (reportId) => `${API_URL}/dynamicreport/${reportId}/parameter`;
const GET_PARAM_VALUES_URL = (reportId, paramId) =>
    `${API_URL}/dynamicreport/${reportId}/parameter/${paramId}/values`;
const EXPORT_REPORT_URL = (reportId) => `${API_URL}/dynamicreport/${reportId}/export`;

export default function DynamicReportList() {
    const [reports, setReports] = useState([]);                   // { id, name, longDescription }
    const [isLoadingReports, setIsLoadingReports] = useState(false);

    const [selectedReportId, setSelectedReportId] = useState(null);
    const [params, setParams] = useState([]);                     // { id, name, label, dataType, isRequired, hasValueSql, hasStaticValues }
    const [isLoadingParams, setIsLoadingParams] = useState(false);

    const [paramOptions, setParamOptions] = useState({});         // { [paramId]: [ { Value, Text }, ... ] }
    const [loadingOptions, setLoadingOptions] = useState({});     // { [paramId]: boolean }

    const [paramValues, setParamValues] = useState({});           // { [paramName]: selectedValue }

    const [isDownloading, setIsDownloading] = useState(false);

    // 1) Rapor listesini bir kez çek
    useEffect(() => {
        setIsLoadingReports(true);
        axios
            .get(GET_REPORTS_URL)
            .then(({ data }) => {
                setReports(data || []);
            })
            .catch((err) => {
                console.error('Raporlar yüklenirken hata:', err);
            })
            .finally(() => {
                setIsLoadingReports(false);
            });
    }, []);

    // 2) Rapor seçildiğinde parametre metalarını çek
    useEffect(() => {
        if (!selectedReportId) {
            setParams([]);
            setParamOptions({});
            setParamValues({});
            return;
        }

        setIsLoadingParams(true);
        axios
            .get(GET_PARAMETERS_URL(selectedReportId))
            .then(({ data }) => {
                setParams(data || []);
                setParamOptions({});
                setParamValues({});
            })
            .catch((err) => {
                console.error('Parametreler yüklenirken hata:', err);
            })
            .finally(() => {
                setIsLoadingParams(false);
            });
    }, [selectedReportId]);

    // 3) Bir parametreye ait dropdown seçeneklerini getir
    const fetchParamOptions = (param) => {
        if (!selectedReportId || !param.hasValueSql) return;

        setLoadingOptions((prev) => ({ ...prev, [param.id]: true }));
        axios
            .post(GET_PARAM_VALUES_URL(selectedReportId, param.id), {})
            .then(({ data }) => {
                setParamOptions((prev) => ({ ...prev, [param.id]: data || [] }));
            })
            .catch((err) => {
                console.error(`Parametre (${param.name}) için seçenekler yükleme hatası:`, err);
            })
            .finally(() => {
                setLoadingOptions((prev) => ({ ...prev, [param.id]: false }));
            });
    };

    // 4) Parametre değişimini işle
    const handleParamChange = (param, value) => {
        setParamValues((prev) => ({ ...prev, [param.name]: value }));

        // Bağımlı parametrelerin seçeneklerini yeniden yüklemek gerekirse
        params.forEach((p) => {
            if (p.id !== param.id && p.hasValueSql) {
                fetchParamOptions(p);
            }
        });
    };

    // 5) Excel indir butonuna basılınca
    const handleDownload = () => {
        if (!selectedReportId) return;

        // Zorunlu parametre kontrolleri
        for (let p of params) {
            if (p.isRequired && (paramValues[p.name] === undefined || paramValues[p.name] === '')) {
                alert(`"${p.label}" alanı zorunludur.`);
                return;
            }
        }

        setIsDownloading(true);
        axios
            .post(
                EXPORT_REPORT_URL(selectedReportId),
                paramValues,
                { responseType: 'blob' }
            )
            .then((res) => {
                const blob = new Blob([res.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                });
                saveAs(blob, `rapor_${selectedReportId}.xlsx`);
            })
            .catch((err) => {
                console.error('Excel indirme hatası:', err);
                alert('Rapor indirilemedi.');
            })
            .finally(() => {
                setIsDownloading(false);
            });
    };


    const selectedReport = reports.find((r) => r.id === selectedReportId);

    return (
        <div className="dynamic-report-container">
            <h2>Dinamik Raporlar</h2>

            {/* Rapor Seçimi */}
            <div className="mb-3">
                <label htmlFor="reportSelect" className="form-label">
                    Rapor Seçiniz:
                </label>
                {isLoadingReports ? (
                    <div>Yükleniyor...</div>
                ) : (
                    <select
                        id="reportSelect"
                        className="form-select"
                        value={selectedReportId || ''}
                        onChange={(e) => {
                            const val = e.target.value;
                            setSelectedReportId(val ? Number(val) : null);
                        }}
                    >
                        <option value="">-- Seçiniz --</option>
                        {reports.map((r) => (
                            <option key={r.id} value={r.id}>
                                {r.name}
                            </option>
                        ))}
                    </select>
                )}
            </div>

            {/* Seçilen raporun uzun açıklaması */}
            {selectedReport && selectedReport.longDescription && (
                <div className="mb-3">
                    <p className="long-description">
                        {selectedReport.longDescription}
                    </p>
                </div>
            )}

            {/* Parametre Formu */}
            {selectedReportId && (
                <div className="param-form border p-3 mb-3">
                    <h5>Parametreler</h5>
                    {isLoadingParams ? (
                        <div>Parametreler yükleniyor...</div>
                    ) : params.length === 0 ? (
                        <div>Bu rapor için parametre bulunamadı.</div>
                    ) : (
                        params.map((param) => (
                            <div key={param.id} className="mb-3">
                                <label htmlFor={`param_${param.id}`} className="form-label">
                                    {param.label}
                                    {param.isRequired && <span className="text-danger"> *</span>}
                                </label>

                                {param.hasValueSql || param.hasStaticValues ? (
                                    <select
                                        id={`param_${param.id}`}
                                        className="form-select"
                                        value={paramValues[param.name] || ''}
                                        onChange={(e) => handleParamChange(param, e.target.value)}
                                        onFocus={() => {
                                            if (!paramOptions[param.id]?.length && param.hasValueSql) {
                                                fetchParamOptions(param);
                                            }
                                        }}
                                        disabled={loadingOptions[param.id]}
                                    >
                                        <option value="">-- Seçiniz --</option>
                                        {(paramOptions[param.id] || []).map((opt) => {
                                            const val = opt.Value ?? Object.values(opt)[0];
                                            const text = opt.Text ?? Object.values(opt)[1] ?? val;
                                            return (
                                                <option key={val} value={val}>
                                                    {text}
                                                </option>
                                            );
                                        })}
                                    </select>
                                ) : param.dataType.toLowerCase() === 'datetime' ? (
                                    <input
                                        type="date"
                                        id={`param_${param.id}`}
                                        className="form-control"
                                        value={paramValues[param.name] || ''}
                                        onChange={(e) => handleParamChange(param, e.target.value)}
                                    />
                                ) : (
                                    <input
                                        type="text"
                                        id={`param_${param.id}`}
                                        className="form-control"
                                        value={paramValues[param.name] || ''}
                                        onChange={(e) => handleParamChange(param, e.target.value)}
                                    />
                                )}

                                {loadingOptions[param.id] && (
                                    <div className="form-text">Seçenekler yükleniyor...</div>
                                )}
                            </div>
                        ))
                    )}

                    <button
                        className="btn btn-primary"
                        onClick={handleDownload}
                        disabled={isDownloading}
                    >
                        {isDownloading ? 'İndiriliyor...' : 'Excel İndir'}
                    </button>
                </div>
            )}
        </div>
    );
}
